"use client";

import { useQuery } from "@tanstack/react-query";

import { ONE_MINUTE_MS } from "#/config/time";
import { type MarketPair, Pairs } from "#/types/markets";

import { fetchMarketPairTickers } from "../kyan.service";
import {
  type MarketPairTicker,
  type MarketPairTickerResponse,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

const createDefaultMarketPairTickerRecord = (): Record<
  MarketPair,
  MarketPairTicker
> => {
  return (Object.values(Pairs) as MarketPair[]).reduce(
    (acc, pair) => {
      acc[pair] = {
        pair,
        price: 0,
        high_24h: 0,
        low_24h: 0,
        price_24h_ago: 0,
        price_change: 0,
        price_change_pct: 0,
      };

      return acc;
    },
    {} as Record<MarketPair, MarketPairTicker>,
  );
};

const transform = (
  data?: MarketPairTickerResponse,
): Record<MarketPair, MarketPairTicker> => {
  return (data?.data ?? []).reduce((acc, ticker) => {
    acc[ticker.pair as MarketPair] = ticker;

    return acc;
  }, createDefaultMarketPairTickerRecord());
};

export const useMarketPairTickers = () => {
  const {
    data = createDefaultMarketPairTickerRecord(),
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["fetchMarketPairTickers"],
    queryFn: () =>
      defaultApiResponseHandler<undefined, MarketPairTickerResponse>({
        serviceName: "Fetch Market Pair Ticker",
        payload: undefined,
        service: fetchMarketPairTickers,
      }),
    select: transform,
    refetchInterval: ONE_MINUTE_MS * 5,
  });

  return {
    data,
    isLoading,
    refetch,
  };
};
